import React from 'react'
import type { MemberListFilters, Organization } from '../../types'
import { SEAT_FILTER_OPTIONS, STATUS_FILTER_OPTIONS, TABLE_COLUMNS } from '../../constants'
import { roleFilterOptions } from '../../utils'
import ColumnFilter from './ColumnFilter'

interface MemberTableHeadProps {
  organizations?: Organization[]
  /** Orgs used to build Role filter options; defaults to `organizations`. */
  roleOrganizations?: Organization[]
  /** Organizations column filter is only used on the All organizations tab. */
  showOrganizationFilter?: boolean
  filters?: MemberListFilters
  onFiltersChange?: (filters: MemberListFilters) => void
}

const MemberTableHead: React.FC<MemberTableHeadProps> = ({
  organizations = [],
  roleOrganizations,
  showOrganizationFilter = false,
  filters,
  onFiltersChange,
}) => {
  const roleScope = roleOrganizations ?? organizations
  const roleOptions = roleFilterOptions(roleScope).map((role) => ({ label: role, value: role }))
  const canFilterOrganizations = showOrganizationFilter && organizations.length > 1
  const organizationOptions = organizations.map((org) => ({ label: org.name, value: org.id }))
  const statusOptions = STATUS_FILTER_OPTIONS.map((status) => ({ label: status, value: status }))
  const seatOptions = SEAT_FILTER_OPTIONS.map((seat) => ({ label: seat, value: seat }))

  const stickyClass = (col: (typeof TABLE_COLUMNS)[number]) => {
    if (col === 'Email') return 'um-refactor__cell-sticky um-refactor__cell-sticky--email'
    if (col === 'Full name') return 'um-refactor__cell-sticky um-refactor__cell-sticky--name'
    if (col === 'Actions') return 'um-refactor__col-actions'
    return undefined
  }

  return (
    <div className="um-refactor__table-head">
      {TABLE_COLUMNS.map((col) => (
        <div key={col} className={stickyClass(col)}>
          <div className="um-refactor__th">
            <span className="um-refactor__th-label">{col}</span>
            {col === 'Role in this org' && filters && onFiltersChange && (
              <ColumnFilter
                ariaLabel="Filter by role"
                options={roleOptions}
                value={filters.roles}
                onChange={(roles) => onFiltersChange({ ...filters, roles: roles as MemberListFilters['roles'] })}
              />
            )}
            {col === 'Organizations' && canFilterOrganizations && filters && onFiltersChange && (
              <ColumnFilter
                ariaLabel="Filter by organization"
                options={organizationOptions}
                value={filters.organizationIds}
                onChange={(organizationIds) => onFiltersChange({ ...filters, organizationIds })}
              />
            )}
            {col === 'Status' && filters && onFiltersChange && (
              <ColumnFilter
                ariaLabel="Filter by status"
                multiple={false}
                options={statusOptions}
                value={filters.status ? [filters.status] : []}
                onChange={(status) => onFiltersChange({
                  ...filters,
                  status: (status[0] || '') as MemberListFilters['status'],
                })}
              />
            )}
            {col === 'Seat' && filters && onFiltersChange && (
              <ColumnFilter
                ariaLabel="Filter by seat"
                multiple={false}
                options={seatOptions}
                value={filters.accountTier ? [filters.accountTier] : []}
                onChange={(accountTier) => onFiltersChange({
                  ...filters,
                  accountTier: (accountTier[0] || '') as MemberListFilters['accountTier'],
                })}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default MemberTableHead
